import { cn } from '@/lib/utils';
import { Bot, User } from 'lucide-react';
import type { ChatMessage as ChatMessageType, PipelineOption } from '@/types/ai-pipeline';
import { PipelineOptionCard } from './PipelineOptionCard';

interface ChatMessageProps {
  message: ChatMessageType;
  onSelectPipeline?: (option: PipelineOption) => void;
  isStreaming?: boolean;
  isGenerating?: boolean;
}

export function ChatMessage({
  message,
  onSelectPipeline,
  isStreaming,
  isGenerating = false,
}: ChatMessageProps) {
  const isUser = message.role === 'user';

  const displayContent = message.content
    .replace(/```json\s*\n?\{[\s\S]*?"suggestions"[\s\S]*?```/g, '')
    .trim();

  return (
    <div className={cn('flex gap-3 p-4', isUser ? 'justify-end' : '')}>
      {!isUser && (
        <div className="shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <Bot className="h-4 w-4 text-primary" />
        </div>
      )}

      <div className={cn('max-w-[80%] space-y-2', isUser ? 'order-first' : '')}>
        <div
          className={cn(
            'rounded-lg px-4 py-2.5 text-sm',
            isUser
              ? 'bg-primary text-primary-foreground ml-auto'
              : 'bg-muted'
          )}
        >
          <div className="whitespace-pre-wrap">{displayContent}</div>
          {isStreaming && !isUser && (
            <span className="inline-block w-1.5 h-4 bg-current animate-pulse ml-0.5" />
          )}
        </div>

        {message.suggestions && message.suggestions.length > 0 && (
          <div className="space-y-2 mt-2">
            {message.suggestions.map(option => (
              <PipelineOptionCard
                key={option.id}
                option={option}
                disabled={isGenerating}
                onSelect={() => onSelectPipeline?.(option)}
              />
            ))}
          </div>
        )}
      </div>

      {isUser && (
        <div className="shrink-0 w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
          <User className="h-4 w-4" />
        </div>
      )}
    </div>
  );
}
